import { handleEmbeds } from '../embed/index.jsx';
import { renderPreview } from '../forum/preview.jsx';
import { initForumUploads } from '../forum/uploads.jsx';
import { removeChildren } from '../html.js';
import { getMeta } from '../meta.js';
import { showMessageBox } from '../msgbox.jsx';
import { getTagsFor, insertTags } from '../parsing.js';
import xhr from '../xhr.js';

(() => {
    const aboutUri = getMeta('profile-about');
    if(typeof aboutUri !== 'string')
        return;

    const aboutTarget = document.querySelector('.js-profile-about');
    if(!(aboutTarget instanceof HTMLElement))
        return;

    let origBody;
    let stopHandlingUploads;
    let form, notice, preview;

    const edit = aboutTarget.querySelector('.js-edit');
    const save = aboutTarget.querySelector('.js-save');
    const cancel = aboutTarget.querySelector('.js-cancel');

    const removeForm = restore => {
        if(stopHandlingUploads) {
            stopHandlingUploads();
            stopHandlingUploads = undefined;
        }

        save.type = 'button';
        save.removeAttribute('form');
        aboutTarget.removeChild(form);

        if(restore && !aboutTarget.contains(origBody)) {
            if(aboutTarget.contains(preview))
                preview.replaceWith(origBody);
            else if(notice !== origBody)
                notice.replaceWith(origBody);
        }

        origBody = form = notice = preview = undefined;
        save.hidden = cancel.hidden = true;
        edit.hidden = false;
    };

    const submitForm = async ev => {
        ev.preventDefault();

        if(ev.target.dataset.disabled)
            return;
        ev.target.dataset.disabled = '1';

        try {
            for(let i = 0; i < ev.target.elements.length; ++i) {
                const element = ev.target.elements[i];
                element.dataset.disabled = element.disabled ? '1' : '';
                element.disabled = true;
            }

            const params = new FormData;
            params.set('format', ev.target.elements.format.value);
            params.set('body', ev.target.elements.body.value);

            const { status, body } = await xhr.patch(aboutUri, { authed: true, csrf: true }, params);
            const lnotice = notice, lpreview = preview;

            if(status === 204) {
                removeForm();
                if(!aboutTarget.contains(lnotice))
                    lpreview.replaceWith(lnotice);
                return;
            }

            if(status === 200) {
                removeForm();
                removeChildren(lpreview);
                lpreview.insertAdjacentHTML('beforeend', body);
                if(!aboutTarget.contains(lpreview))
                    lnotice.replaceWith(lpreview);
                return;
            }

            switch(status) {
            case 404: throw new Error('Profile not found.');
            case 403: throw new Error('You are not allowed to edit this about section.');
            case 400: throw new Error(body?.error?.text ?? 'A formatting error occurred.');
            default: throw new Error(`Received unexpected HTTP status code ${status}.`);
            }
        } catch(ex) {
            await showMessageBox(ex, 'About');
        } finally {
            for(let i = 0; i < ev.target.elements.length; ++i) {
                const element = ev.target.elements[i];
                element.disabled = element.dataset.disabled !== '';
                delete element.dataset.disabled;
            }

            delete ev.target.dataset.disabled;
        }
    };

    edit.onclick = async () => {
        cancel.disabled = save.disabled = edit.disabled = true;
        try {
            notice = aboutTarget.querySelector('.js-notice') ?? <div class="profile-about-notice"><em>Tell us about yourself!</em></div>;
            preview = aboutTarget.querySelector('.js-preview') ?? <div class="profile-about-body" />;

            // hack
            for(const elem of preview.querySelectorAll('audio, video')) {
                elem.pause();
                elem.currentTime = 0;
                elem.autoplay = false;
            }

            origBody = aboutTarget.contains(notice) ? notice : preview.cloneNode(true);

            const { status, body } = await xhr.get(aboutUri, { authed: true, csrf: true, type: 'json' });

            if(status !== 200) switch(status) {
            case 404: throw new Error('Profile not found.');
            case 403: throw new Error('You are not allowed to edit this about section.');
            default: throw new Error(`Received unexpected HTTP status code ${status}.`);
            }

            let bodyElem, formatElem, markupBtns, previewTimeout;
            let { body: lastBody, format: lastFormat } = body;

            function switchButtons(format) {
                removeChildren(markupBtns);
                for(const tag of getTagsFor(format))
                    markupBtns.appendChild(<button class="profile-about-form-markup-button" type="button" title={tag.summary} onclick={() => void insertTags(bodyElem, tag.open, tag.close)}>
                        <i class={tag.icon} />
                    </button>);
            }

            async function renderAboutPreview() {
                clearTimeout(previewTimeout);
                previewTimeout = null;

                const frag = await renderPreview(formatElem.value, bodyElem.value, 'about');

                if(bodyElem.value.trim() === '') {
                    if(aboutTarget.contains(preview))
                        preview.replaceWith(notice);
                } else if(aboutTarget.contains(notice)) {
                    notice.replaceWith(preview);
                }

                preview.classList.toggle('bbcode', formatElem.value === 'bb');
                preview.classList.toggle('markdown', formatElem.value === 'md');

                removeChildren(preview);
                preview.appendChild(frag);

                setTimeout(() => void handleEmbeds(document.querySelectorAll('.js-msz-embed-media')), 0);
            }

            aboutTarget.appendChild(form = <form class="container profile-about-form" id="_about" onsubmit={submitForm}>
                <div class="profile-about-form-toolbar">
                    <div class="profile-about-form-format">
                        {formatElem = <select class="input__select" name="format" onchange={() => {
                            switchButtons(formatElem.value);
                            if(lastFormat !== formatElem.value)
                                renderAboutPreview().then(() => { lastFormat = formatElem.value });
                        }}>{...body.formats.map(format => <option value={format.value} selected={format.value === body.format}>{format.label}</option>)}</select>}
                    </div>
                    {markupBtns = <div class="profile-about-form-markup"></div>}
                </div>
                {/* TODO: maxlength should be checked using grapheme clusters like on the server side */}
                {bodyElem = <textarea class="profile-about-form-body" name="body" maxlength={body.max} placeholder="Describe your hopes and dreams here..." oninput={ev => {
                    clearTimeout(previewTimeout);
                    previewTimeout = setTimeout(() => void renderAboutPreview().then(() => { lastBody = bodyElem.value; }), 500);
                }} onkeydown={ev => {
                    if(ev.key === 'Enter' && ev.shiftKey && !ev.altKey && !ev.ctrlKey && !ev.metaKey) {
                        ev.preventDefault();
                        form.requestSubmit();
                    }
                }}>{body.body}</textarea>}
            </form>);

            switchButtons(body.format);
            stopHandlingUploads = initForumUploads(form, bodyElem, formatElem, () => void renderAboutPreview());

            save.type = 'submit';
            save.setAttribute('form', form.id);

            save.hidden = cancel.hidden = false;
            edit.hidden = true;
        } catch(ex) {
            await showMessageBox(ex, 'About');
        } finally {
            cancel.disabled = save.disabled = edit.disabled = false;
        }
    };
    cancel.onclick = async () => {
        cancel.disabled = save.disabled = edit.disabled = true;
        try {
            removeForm(true);
        } catch(ex) {
            await showMessageBox(ex, 'About');
        } finally {
            cancel.disabled = save.disabled = edit.disabled = false;
        }
    };
})();
